import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, Upload, Building2, Search, Package } from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== Brand Form Modal (exported for reuse in /category/:id admin controls) =====
export const BrandFormModal = ({ brand, categories, onClose, onSaved }) => {
  const [name, setName] = useState(brand?.name_ar || "");
  // Multi-select categories. Falls back to legacy single `category` field.
  const initialCats = (brand?.categories && brand.categories.length > 0)
    ? brand.categories
    : (brand?.category ? [brand.category] : []);
  const [selectedCats, setSelectedCats] = useState(initialCats);
  const [imagePath, setImagePath] = useState(brand?.image_path || null);
  const [imagePreview, setImagePreview] = useState(brand?.image_path ? `${API}/files/${brand.image_path}` : null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggleCat = (catName) => {
    setSelectedCats((prev) =>
      prev.includes(catName) ? prev.filter((c) => c !== catName) : [...prev, catName]
    );
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("صورة فقط"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("أقل من 5 ميغا"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload-image`, fd);
      setImagePath(data.path);
      setImagePreview(URL.createObjectURL(file));
      toast.success("تم رفع الصورة");
    } catch {
      toast.error("فشل رفع الصورة");
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("اسم الشركة مطلوب"); return; }
    if (selectedCats.length === 0) { toast.error("اختر قسم واحد على الأقل"); return; }
    setBusy(true);
    try {
      if (brand) {
        await axios.patch(`${API}/admin/brands/${brand.id}`, {
          name_ar: name.trim(),
          categories: selectedCats,
          image_path: imagePath,
        });
        toast.success("تم تحديث الشركة");
      } else {
        await axios.post(`${API}/admin/brands`, {
          name_ar: name.trim(),
          categories: selectedCats,
          image_path: imagePath,
        });
        toast.success("تمت إضافة الشركة");
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" dir="rtl" data-testid="brand-form-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-[#1E3A5F]/20">
          <h3 className="font-bold text-xl text-[#1E3A5F] flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {brand ? "تعديل الشركة" : "إضافة شركة جديدة"}
          </h3>
          <button onClick={onClose} data-testid="brand-form-close" className="text-[#5C6E64]"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">اسم الشركة <span className="text-[#E85D45]">*</span></label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              data-testid="brand-name-input"
              placeholder="مثال: البوادي، الريان، قاله..."
              className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] bg-white" />
          </div>

          <div>
            <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">
              الأقسام التي تنتج فيها الشركة <span className="text-[#E85D45]">*</span>
              <span className="text-[10px] font-normal text-[#5C6E64] mr-1">
                (يمكن اختيار أكثر من قسم — الشركة ستظهر في كل قسم تختاره)
              </span>
            </label>
            <div className="grid grid-cols-2 gap-2" data-testid="brand-categories-grid">
              {categories.map((c) => {
                const active = selectedCats.includes(c.name);
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => toggleCat(c.name)}
                    data-testid={`brand-cat-${c.name}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-sm font-bold transition-all ${
                      active
                        ? "bg-[#1E3A5F] border-[#1E3A5F] text-white shadow-md"
                        : "bg-white border-[#E4EBE6] text-[#1E3A5F] hover:border-[#3B82F6]"
                    }`}
                  >
                    <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                      active ? "bg-white border-white" : "border-[#3B82F6]"
                    }`}>
                      {active && <svg viewBox="0 0 16 16" className="w-3 h-3 text-[#1E3A5F]" fill="currentColor"><path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"/></svg>}
                    </span>
                    <span className="truncate">{c.name_ar}</span>
                  </button>
                );
              })}
            </div>
            {selectedCats.length > 0 && (
              <p className="text-[11px] text-[#1c7c3a] mt-2 font-bold">
                <span dir="ltr">{selectedCats.length}</span> قسم محدّد
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">شعار / صورة الشركة</label>
            <label className="block border-2 border-dashed border-[#BAE6FD] rounded-2xl p-3 hover:bg-[#E0F2FE] cursor-pointer text-center" data-testid="brand-upload-area">
              {imagePreview ? (
                <img src={imagePreview} alt="logo" className="mx-auto h-28 object-contain" />
              ) : (
                <div className="py-4">
                  <Upload className="w-8 h-8 text-[#5C6E64] mx-auto mb-1" />
                  <p className="text-xs text-[#5C6E64]">اضغط لرفع شعار الشركة</p>
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleImage} className="hidden" data-testid="brand-upload-input" />
              {uploading && <p className="text-xs text-[#3B82F6] mt-1">جاري الرفع...</p>}
            </label>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button onClick={submit} disabled={busy} data-testid="brand-save"
            className="flex-1 bg-[#1E3A5F] text-white font-bold py-2 rounded-full disabled:opacity-50">
            {busy ? "جاري الحفظ..." : brand ? "حفظ التعديل" : "إضافة الشركة"}
          </button>
          <button onClick={onClose} className="bg-[#F3F6F4] text-[#5C6E64] font-bold py-2 px-4 rounded-full">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

// ===== Main Brands Tab =====
const BrandsTab = () => {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [bRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/brands`),
        axios.get(`${API}/categories`),
      ]);
      setBrands(bRes.data || []);
      setCategories(cRes.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (brand) => {
    if (!(await confirmDialog(`حذف شركة "${brand.name_ar}"؟\n\nالمنتجات (${brand.product_count}) لن تُحذف — ستبقى في القسم بدون شركة.`))) return;
    try {
      const { data } = await axios.delete(`${API}/admin/brands/${brand.id}`);
      toast.success(`تم حذف الشركة (${data.products_detached} منتج مفصول)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الحذف");
    }
  };

  const visible = useMemo(() => {
    let arr = brands;
    if (filterCategory) {
      arr = arr.filter((b) => {
        const cats = (b.categories && b.categories.length > 0) ? b.categories : (b.category ? [b.category] : []);
        return cats.includes(filterCategory);
      });
    }
    if (searchQ.trim()) {
      const q = searchQ.trim().toLowerCase();
      arr = arr.filter((b) => (b.name_ar || "").toLowerCase().includes(q));
    }
    return arr;
  }, [brands, filterCategory, searchQ]);

  // group by category — a brand with N categories appears under each of them
  const byCategory = useMemo(() => {
    const map = {};
    for (const b of visible) {
      const cats = (b.categories && b.categories.length > 0) ? b.categories : (b.category ? [b.category] : ["بدون قسم"]);
      for (const cat of cats) {
        if (!map[cat]) map[cat] = [];
        map[cat].push(b);
      }
    }
    return map;
  }, [visible]);

  const totalProducts = brands.reduce((s, b) => s + (b.product_count || 0), 0);

  return (
    <div className="space-y-4" data-testid="brands-tab" dir="rtl">
      <div className="bg-white rounded-2xl p-4 border-2 border-[#1E3A5F]/20 shadow-md">
        <div className="flex flex-wrap items-center justify-between mb-4 pb-3 border-b-2 border-[#1E3A5F]/20 gap-2">
          <h3 className="font-heading font-bold text-xl text-[#1E3A5F] flex items-center gap-2">
            <Building2 className="w-5 h-5" />شركات المنتجات
          </h3>
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="add-brand-btn"
            className="bg-[#1E3A5F] hover:bg-[#152a45] text-white font-bold px-4 py-2 rounded-full flex items-center gap-2">
            <Plus className="w-4 h-4" />إضافة شركة جديدة
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
          <div className="bg-[#1E3A5F] text-white rounded-xl p-3">
            <p className="text-[10px] opacity-80">عدد الشركات</p>
            <p className="font-black text-xl">{brands.length}</p>
          </div>
          <div className="bg-[#7ED321] text-white rounded-xl p-3">
            <p className="text-[10px] opacity-90">إجمالي المنتجات</p>
            <p className="font-black text-xl" dir="ltr">{totalProducts}</p>
          </div>
          <div className="bg-[#3B82F6] text-white rounded-xl p-3">
            <p className="text-[10px] opacity-90">عدد الأقسام</p>
            <p className="font-black text-xl">{categories.length}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
            <input type="text" value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
              data-testid="brands-search"
              placeholder="ابحث عن شركة..."
              className="w-full pr-9 pl-4 py-2 rounded-full bg-[#F3F6F4] border border-[#E4EBE6] text-sm" />
          </div>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
            data-testid="brands-category-filter"
            className="px-3 py-2 rounded-full bg-[#F3F6F4] border border-[#E4EBE6] text-sm">
            <option value="">كل الأقسام</option>
            {categories.map((c) => (<option key={c.id} value={c.name}>{c.name_ar}</option>))}
          </select>
        </div>

        {loading ? (
          <p className="text-center py-8 text-[#5C6E64]">جاري التحميل...</p>
        ) : visible.length === 0 ? (
          <div className="text-center py-12 text-[#5C6E64]">
            <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{searchQ || filterCategory ? "لا توجد نتائج" : "لا توجد شركات بعد — اضغط 'إضافة شركة جديدة'"}</p>
          </div>
        ) : (
          <div className="space-y-5">
            {Object.entries(byCategory).map(([catName, list]) => {
              const cat = categories.find((c) => c.name === catName);
              return (
                <div key={catName}>
                  <h4 className="font-bold text-sm text-[#1E3A5F] mb-2 pb-1 border-b border-[#E4EBE6]">
                    {cat?.name_ar || catName} <span className="text-[#5C6E64] font-normal">({list.length} شركة)</span>
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {list.map((b) => {
                      const brandCats = (b.categories && b.categories.length > 0) ? b.categories : (b.category ? [b.category] : []);
                      return (
                      <div key={`${catName}-${b.id}`} data-testid={`brand-${b.id}`}
                        className="bg-white border-2 border-[#E4EBE6] rounded-2xl p-3 hover:shadow-lg transition-shadow relative">
                        {brandCats.length > 1 && (
                          <span
                            title={`موجودة في ${brandCats.length} أقسام`}
                            className="absolute top-2 left-2 bg-[#F5A623] text-white text-[10px] font-black px-2 py-0.5 rounded-full z-10 flex items-center gap-1"
                          >
                            <span dir="ltr">{brandCats.length}</span> أقسام
                          </span>
                        )}
                        <div className="aspect-square flex items-center justify-center mb-2 bg-gradient-to-br from-[#F0F9FF] to-[#E0F2FE] rounded-xl overflow-hidden">
                          {b.image_path ? (
                            <img src={`${API}/files/${b.image_path}`} alt={b.name_ar} className="w-full h-full object-contain" />
                          ) : (
                            <Building2 className="w-12 h-12 text-[#3B82F6]" />
                          )}
                        </div>
                        <p className="font-bold text-center text-[#1E3A5F] text-sm">{b.name_ar}</p>
                        <p className="text-xs text-center text-[#5C6E64] flex items-center justify-center gap-1 mt-1">
                          <Package className="w-3 h-3" />{b.product_count} منتج
                        </p>
                        <div className="flex gap-1 mt-2">
                          <button onClick={() => { setEditing(b); setShowForm(true); }}
                            data-testid={`edit-brand-${b.id}`}
                            className="flex-1 bg-[#3B82F6] hover:bg-[#2563eb] text-white text-xs font-bold py-1.5 rounded-full flex items-center justify-center gap-1">
                            <Edit2 className="w-3 h-3" />تعديل
                          </button>
                          <button onClick={() => handleDelete(b)}
                            data-testid={`delete-brand-${b.id}`}
                            className="flex-1 bg-[#E85D45] hover:bg-[#c14a36] text-white text-xs font-bold py-1.5 rounded-full flex items-center justify-center gap-1">
                            <Trash2 className="w-3 h-3" />حذف
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <BrandFormModal
          brand={editing}
          categories={categories}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default BrandsTab;
